import {Component, Input, OnInit, Output, EventEmitter, ViewChild, ElementRef} from '@angular/core';
import * as L from 'leaflet';
import domtoimage from 'dom-to-image';
import {MessageCrudService} from '@evomatik/evk-common';
import {EstadoMapa} from "../modelos/EstadoMapa";
import {Punto} from "../modelos/Punto";
import {Marcador} from "../modelos/Marcador";
import {CroquisSizeConfig} from "../modelos/CroquisSizeConfig";
import {EvkMapasService} from "../../evk-mapas.service";

@Component({
  selector: 'evk-mapa',
  templateUrl: './evk-mapa.component.html',
  styleUrls: ['./evk-mapa.component.scss']
})
export class EvkMapaComponent implements OnInit {
  @ViewChild('map') mapElement: ElementRef;
  @Input() estadoMapa: EstadoMapa;
  @Output() clickEnMapa: EventEmitter<Punto> = new EventEmitter<Punto>();
  private mapa: L.Map;
  private maxZoom = 30;
  private controlMarcadores = new Map();

  constructor(private messageService: MessageCrudService, private mapasService: EvkMapasService) {
    this.initEstadoMapa();
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.initMapa();
  }

  private initEstadoMapa() {
    if (!this.estadoMapa) {
      this.estadoMapa = EstadoMapa.construirEstadoInicial();
    }
  }

  private initMapa() {
    let estado = this.estadoMapa.getEstadoParaLeafletjs();
    const mapa = L.map(this.mapElement.nativeElement, estado);
    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: this.maxZoom,
        zIndex: 1,
        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }
    );
    L.control.scale().addTo(mapa);
    L.control.zoom({position: "topright"}).addTo(mapa);
    tiles.addTo(mapa);
    mapa.on("click", this.clickHechoEnMapa.bind(this));

    this.mapasService.geoJsonLayer = L.geoJSON().addTo(mapa);
    this.mapasService.mapa = mapa;
    setTimeout(() => {
      mapa.invalidateSize();
      this.moverAPunto(this.estadoMapa.centro);
    });
  }

  private clickHechoEnMapa(event: any) {
    let punto;
    if (event && event.latlng) {
      punto = new Punto(event.latlng.lat, event.latlng.lng)
    }
    this.clickEnMapa.emit(punto);
  }

  private crearIconoParaMarcador() {
    let icono = L.icon({
      iconAnchor: [12, 41],
      iconSize: [25, 41],
      popupAnchor: [1, -34],
      shadowSize: [25, 41],
      tooltipAnchor: [16, -28],
      iconUrl: 'leaflet/marker-shadow.png',
      shadowUrl: 'leaflet/marker-icon.png',
      iconRetinaUrl: 'leaflet/marker-icon-2x.png'
    });
    return icono;
  }

  public agregarMarcador(marcador: Marcador) {
    if (marcador) {
      let existeMarcador = this.controlMarcadores.has(marcador.id);
      let marker = null;
      if (!existeMarcador) {
        this.estadoMapa.agregarMarcador(marcador);
        let opciones = marcador.getOpcionesMarcadorParaLeafletjs();
        marker = this.mapasService.agregarMarker(marcador, opciones);
        let icono = this.crearIconoParaMarcador();
        marker.setIcon(icono);
        this.controlMarcadores.set(marcador.id, marker);
      }
      return marker;
    }
    return undefined;
  }

  public quitarMarcador(idMarcador: string) {
    if (idMarcador) {
      let existeMarcador = this.controlMarcadores.has(idMarcador);
      if (existeMarcador) {
        let marcadorMapa = this.controlMarcadores.get(idMarcador);
        this.mapa.removeLayer(marcadorMapa);
        this.controlMarcadores.delete(idMarcador);
        this.estadoMapa.eliminarMarcador(idMarcador);
      }
    }
  }

  public moverAPunto(punto: Punto) {
    this.mapasService.moverAPunto(punto);
  }

  public moverAPuntoConZoom(punto: Punto, zoom: number) {
    this.mapasService.moverAPuntoConZoom(punto, zoom);
  }

  public actualizarPuntoDeMarcador(marcador: Marcador, nuevoPunto: Punto) {
    if (marcador && nuevoPunto && this.controlMarcadores.has(marcador.id)) {
      let marker = this.controlMarcadores.get(marcador.id);
      var newLatLng = new L.LatLng(nuevoPunto.latitud, nuevoPunto.longitud);
      marker.setLatLng(newLatLng);
      return true;
    }
    return false;
  }

  public obtenerImagenDeMapaComoPng(size: CroquisSizeConfig) {
    this.messageService.snackbarLoading('Generando imagen de mapa...');
    var node = document.getElementById('map');
    if (node != null) {
      let scaleX = size.width / node.offsetWidth;
      let scaleY = size.height / node.offsetHeight;
      let scale = scaleX + ',' + scaleY;
      let props = {
        width: node.offsetWidth * scaleX,
        height: node.offsetHeight * scaleY,
        style: {
          'transform': 'scale(' + scale + ')',
          'transform-origin': 'top left',
          'width': node.offsetWidth + 'px',
          'height': node.offsetHeight + 'px'
        }
      }
      let zoom = <HTMLElement>document.getElementsByClassName('leaflet-control-zoom')[0];
      if (zoom) {
        zoom.style.visibility = "hidden";
      }
      setTimeout(() => {
        zoom.style.visibility = "visible";
        this.messageService.snackbarDismiss();
      }, 2000);
      return domtoimage.toPng(node, props);
    }
    return undefined;
  }

  public obtenerImagenDeMapaComoSvg() {
    var node = document.getElementById('map');
    if (node != null) {
      let zoom = <HTMLElement>document.getElementsByClassName('leaflet-control-zoom')[0];
      if (zoom) {
        zoom.style.visibility = "hidden";
      }
      setTimeout(function () {
        zoom.style.visibility = "visible";
      }, 2000);
      return domtoimage.toSvg(node);
    }
    return undefined;
  }

  private filter() {
    return false;
  }

  public getPosicionDeMarcador(marcador: Marcador): Punto | null {
    if (marcador && this.controlMarcadores.has(marcador.id)) {
      let marker = this.controlMarcadores.get(marcador.id);
      let latLng = marker.getLatLng();
      let punto = new Punto(latLng.lat, latLng.lng);
      return punto;
    }
    return null;
  }

  onEachFeature(feature, layer) {
    // does this feature have a property named popupContent?
    if (feature.properties && feature.properties.popupContent) {
      layer.bindPopup(feature.properties.popupContent);
    }
  }
}
